/* istanbul ignore file */
import React, { useCallback, useEffect, useRef } from 'react'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
// @ts-ignore no types
import analytics from '@adc/analytics'
import * as blueprint from '@adc/blueprint'
import { Placeholder } from 'semantic-ui-react'
import type { Listing, Address, CurrentTargetAudience } from '@adc/sdk/types'
import Text from '../../elementals/text'
import cls from './cardPartsStyles.scss'
import { colorVars } from '../../../constants/colorVars'
import { ListingBadgesTags } from '../badges'
import PropertySaveIndicator from '../../property/propertySaveIndicator'
import { images } from '../../../images/exported'
import ListingIndicator from '../listingIndicator'
import DualTrackLobIndicators, {
  buildSiblingListingPaths,
} from '../dualTrackLobIndicators'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'
import { formatListingImgUrl } from '../utils/formatters'
import { ListingCardAnalyticsAttributes } from './index'
import { AutomationTarget } from '../../../types'

const { getAddressLine, getDisplayedValue, MISSING_VALUE_LABEL } =
  blueprint.listing.utils.card

const cx = classnames.bind(cls)

export interface ListingCardLinkProps {
  onClick?: (
    e: ListingCardAnalyticsAttributes &
      Partial<ReturnType<typeof analytics.standardTracking.listing>>
  ) => void
  onMouseLeave?: (e?: React.MouseEvent) => void
  href?: string
}

export enum HOVER_ACTIONS {
  SHOW = 'SHOW',
  HIDE = 'HIDE',
  TOGGLE = 'TOGGLE',
}

interface PropertyInfoProps extends AutomationTarget {
  listing: Listing
  isHoverMode?: boolean
  className?: string
  isCancelled?: boolean
}

export const PropertyInfo = ({
  listing,
  elmId,
  isHoverMode = false,
  className,
  isCancelled = false,
}: PropertyInfoProps) => {
  const {
    total_bedrooms: beds,
    total_bathrooms: baths,
    square_footage: sqft,
    lot_size: lotSize,
    year_built: yearBuilt,
  } = listing.primary_property?.summary || {}

  return (
    <div className={cx('property-info', className)}>
      {!isCancelled && (
        <div className={cx('primary-info')}>
          {!!beds && (
            <div
              className={cx({
                separator: !!(baths || sqft),
              })}
            >
              <span
                className={cx('value')}
                data-elm-id={`card_auction_beds_${elmId}`}
              >
                {beds}
              </span>{' '}
              bd
            </div>
          )}
          {!!beds && !!baths && (
            <div className={cx({ separator: !!sqft })}>
              <span
                className={cx('value')}
                data-elm-id={`card_auction_baths_${elmId}`}
              >
                {baths}
              </span>{' '}
              ba
            </div>
          )}
          {!!sqft && (
            <div className={cx('truncate')}>
              <span
                className={cx('value')}
                data-elm-id={`card_auction_sqft_${elmId}`}
              >
                {Number(sqft).toLocaleString()}
              </span>{' '}
              sq. ft.
            </div>
          )}
        </div>
      )}
      {isHoverMode && (lotSize || yearBuilt) && (
        <div className={cx('secondary-info')}>
          {lotSize && (
            <div className={cx({ separator: !!yearBuilt })}>
              Lot Size:{' '}
              <span className={cx('value')}>{`${lotSize} Acres`}</span>
            </div>
          )}
          {yearBuilt && (
            <div>
              Year Built: <span className={cx('value')}>{yearBuilt}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface ListingCardIndicatorProps {
  listing: Listing
  className?: string
  [key: string]: any
}

// TEMPORARY dual-track LOB row (search-only; removed when SRP is property-based).
// Dual-track LOB indicators live in their own reusable component
// (`dualTrackLobIndicators`). Re-exported here so ListingCard keeps its
// inline (row) treatment; other surfaces import the component directly.
export { buildSiblingListingPaths }

export const LobIndicatorRow = ({
  listing,
  elmId,
}: {
  listing: Listing
  elmId?: string
}) => (
  <DualTrackLobIndicators
    listing={listing}
    elmId={elmId}
    direction='row'
    bold
  />
)

export const ListingCardIndicator = ({
  listing,
  className,
  ...restProps
}: ListingCardIndicatorProps) => {
  const shouldDisplaySmartSaleLogo =
    blueprint.listing.utils.isSmartSale(listing) &&
    !blueprint.listing.utils.isSparkOffer(listing)
  return (
    <div className={cx('listing-indicator-row')}>
      <ListingIndicator
        {...restProps}
        className={cx('asset-info', className)}
        hideAuctionDate
        hideAuctionType
        listing={listing}
      />
      {Boolean(shouldDisplaySmartSaleLogo) && (
        <div className={cx('smart-sale-logo-container')}>
          <img src={images.smartsaleLogoIcon} alt='Smart Sale' />
        </div>
      )}
    </div>
  )
}

// ------- NEW PROPERTY CARD PARTS ------

interface LeftSidePropertyValueProps {
  listing: Listing
  isPopup?: boolean
  featureFlags?: Record<string, any>
}

export const LeftSidePropertyValue = ({
  listing,
  isPopup = false,
  featureFlags,
}: LeftSidePropertyValueProps) => {
  const displayedValues = getDisplayedValue(listing, { featureFlags })

  return (
    <div className={cx('property-value', { 'popup-property-value': isPopup })}>
      <span
        title={displayedValues.amount}
        className={cx('property-value-amount', {
          muted: displayedValues.amount === MISSING_VALUE_LABEL,
        })}
      >
        {displayedValues.amount ? displayedValues.amount : 'TBD'}
      </span>
      <span className={cx('amount-label')}>{displayedValues.label}</span>
    </div>
  )
}

interface PropertyValueProps {
  elmId?: string
  featureFlags?: Record<string, any>
  isCancelled?: boolean
  isHorizontalView?: boolean
  isSaved?: boolean
  listing: Listing
  onSaveToggle: (propertyId: string, isSaved: boolean) => void
  showSave?: boolean
  stopPropagationClick?: (e: React.MouseEvent) => void
}

export const PropertyValue = ({
  elmId,
  featureFlags,
  isCancelled,
  isSaved,
  listing,
  onSaveToggle,
  showSave,
  stopPropagationClick,
}: PropertyValueProps) => {
  return (
    <div className={cx('property-value-container')}>
      <LeftSidePropertyValue listing={listing} featureFlags={featureFlags} />
      {showSave && listing.primary_property && (
        <span onClick={stopPropagationClick}>
          <PropertySaveIndicator
            elmId={elmId || ''}
            isCancelled={isCancelled}
            isSaved={isSaved}
            onSaveToggle={onSaveToggle}
            property={listing.primary_property}
            whiteBackground
          />
        </span>
      )}
    </div>
  )
}

interface AddressInfoLineProps {
  address?: Listing['formatted_address']
  elmId?: string
  className?: string
  isCancelled?: boolean
}

export const AddressInfoLine = ({
  address = [],
  elmId,
  className,
  isCancelled,
}: AddressInfoLineProps) => {
  if (!address?.length || address.length < 2) return null

  const cleanedSecondLine = address[1]?.substring(
    0,
    address[1].lastIndexOf(',')
  )
  const fullAddress = `${address[0]}, ${cleanedSecondLine}`

  return (
    <Text.Body
      as='h3'
      elmId={`address_line_${elmId}`}
      className={cx('property-address-line', className)}
      color={colorVars.utkColorGray}
      size='small'
      weight='normal'
    >
      {!isCancelled ? fullAddress : ''}
    </Text.Body>
  )
}

interface CardImageProps {
  listing: Listing
  showImageCount?: boolean
  loading?: boolean
  maxBadges?: number
  isHorizontalView?: boolean
  className?: string
  featureFlags?: Record<string, any>
  verifiedImage?: string
  updateVerifiedImage?: (listingId: string, imageUrl: string | null) => void
  viewingFrom?: CurrentTargetAudience
  badgeClassName?: string
  elmId?: string
  isCompact?: boolean
}

export const CardImage = ({
  listing,
  loading,
  maxBadges,
  isHorizontalView = false,
  className = '',
  featureFlags = {},
  verifiedImage,
  updateVerifiedImage,
  viewingFrom = graph.enums.CurrentTargetAudience.Buyer,
  badgeClassName = '',
  isCompact = false,
}: CardImageProps) => {
  const altTextImage = getAddressLine(
    listing.seller_property as Partial<Address>
  )

  const containerRef = useRef<HTMLDivElement>(null)

  const checkCardScrollWidth = useCallback(() => {
    if (!containerRef.current) {
      return
    }
    return containerRef.current.scrollWidth
  }, [containerRef.current])

  const auxPhoto = useBackgroundImageWithFallback(
    listing.primary_photo,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    verifiedImage,
    // We pass a callback function to the hook to handle the possibility of this component
    // unmounting before the auxPhoto has finished verifying (possible due to the SRP Dynamic
    // card list which ofter refreshes the entire list while scrolling)
    () => updateVerifiedImage && updateVerifiedImage(listing.listing_id, null)
  )

  let primaryPhoto
  if (verifiedImage) {
    /* WHY: the verifiedImage prop is here to allow the parent app to "cache" already
      verified photos. This is useful due to the fact that apps like resi-search reload 
      the entire Listing Card list for even small changes, which means the CardImage 
      component is mounted again for every change. This combined with the fact that  
      useBackgroundImageWithFallback works asynchronously, it can potentially cause a card
      list to flicker while loading the valid URL on any state change on SRP, even just 
      hovering over a card.  If verifiedImage and updateVerified image are not set, this can
      be safely ignored */
    primaryPhoto = verifiedImage
  } else {
    primaryPhoto = auxPhoto
  }

  useEffect(() => {
    if (updateVerifiedImage && auxPhoto !== verifiedImage) {
      updateVerifiedImage(listing.listing_id, auxPhoto)
    }
  }, [auxPhoto, verifiedImage, updateVerifiedImage, listing.listing_id])

  const listingPhotoUrl = formatListingImgUrl(primaryPhoto, {
    width: 282,
  })

  return loading ? (
    <div
      className={cx(
        'property-card-image-container',
        {
          'horizontal-view': isHorizontalView,
          'compact-image': isCompact,
        },
        className
      )}
    >
      <Placeholder fluid style={{ height: '100%' }}>
        <Placeholder.Image />
      </Placeholder>
    </div>
  ) : (
    <div
      ref={containerRef}
      className={cx(
        'property-card-image-container',
        {
          'horizontal-view': isHorizontalView,
          'compact-image': isCompact,
        },
        className
      )}
    >
      <span
        className={cx('property-card-image-background')}
        style={{
          backgroundImage: `url(${listingPhotoUrl})`,
        }}
        role='img'
        aria-label={altTextImage}
      />
      {!isHorizontalView && (
        <ListingBadgesTags
          listing={listing}
          isHorizontalView={isHorizontalView}
          maxBadges={maxBadges}
          viewingFrom={viewingFrom}
          className={badgeClassName}
          getContainerScrollWidth={checkCardScrollWidth}
        />
      )}
    </div>
  )
}
